import type { StageFormValues } from '../service-order-form';

import { PencilSimple } from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';
import { Text } from 'src/components';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { formatDayInput } from 'src/util';

type Props = {
  stage: StageFormValues;
  onEdit: () => void;
};

const PLACEHOLDER = '#999999';

/**
 * One stage as the Stages card lists them: its name and three dates, with a
 * pencil to edit it.  On a narrow screen the dates stack under the name with
 * their labels, rather than squeezing into columns.
 */
export default function StageRow({ stage, onEdit }: Props) {
  const name = stage.name === '' ? 'e.g. Acceptance Testing 1' : stage.name;

  return (
    <>
      <html.div style={[Style.row, Style.alignCenter, styles.row]}>
        <Text
          style={[
            styles.cell,
            styles.nameCell,
            stage.name === '' && styles.placeholder,
          ]}
        >
          {name}
        </Text>
        <DateCell date={stage.startDate} style={styles.dateCell} />
        <DateCell date={stage.estimatedFinishDate} style={styles.dateCell} />
        <DateCell date={stage.finishDate} style={styles.dateCell} />
        <html.button
          style={styles.edit}
          onClick={onEdit}
          aria-label="Edit stage"
        >
          <PencilSimple size={20} weight="regular" color={color.neutral80} />
        </html.button>
      </html.div>

      <html.div style={[Style.col, styles.stack]}>
        <html.div style={[Style.row, Style.alignCenter, styles.stackRow]}>
          <Text
            style={[
              styles.cell,
              styles.stackName,
              stage.name === '' && styles.placeholder,
            ]}
          >
            {name}
          </Text>
          <html.button
            style={styles.edit}
            onClick={onEdit}
            aria-label="Edit stage"
          >
            <PencilSimple size={20} weight="regular" color={color.neutral80} />
          </html.button>
        </html.div>
        <StackedDate label="Start" date={stage.startDate} />
        <StackedDate
          label="Estimated finish"
          date={stage.estimatedFinishDate}
        />
        <StackedDate label="Finish" date={stage.finishDate} />
      </html.div>
    </>
  );
}

function DateCell({
  date,
  style,
}: {
  date: string | null;
  style?: css.StyleXStyles;
}) {
  const shown = date === null ? 'Set date' : formatDayInput(date) ?? 'Set date';
  return (
    <Text style={[styles.cell, style, date === null && styles.placeholder]}>
      {shown}
    </Text>
  );
}

function StackedDate({ label, date }: { label: string; date: string | null }) {
  const shown = date === null ? 'Set date' : formatDayInput(date) ?? 'Set date';
  return (
    <html.div style={[Style.row, Style.alignCenter, styles.stackRow]}>
      <Text style={[styles.cell, styles.stackLabel]}>{label}</Text>
      <Text style={[styles.cell, date === null && styles.placeholder]}>
        {shown}
      </Text>
    </html.div>
  );
}

const styles = css.create({
  row: {
    gap: 8,
    minHeight: 40,
    display: {
      default: 'flex',
      '@media (max-width: 640px)': 'none',
    },
  },
  stack: {
    gap: 8,
    display: {
      default: 'none',
      '@media (max-width: 640px)': 'flex',
    },
  },
  stackRow: {
    gap: 8,
    minHeight: 28,
  },
  cell: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: '1.3',
    color: '#1a1a1a',
  },
  nameCell: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  dateCell: {
    flexShrink: 0,
    width: 140,
  },
  stackName: {
    flexGrow: 1,
    minWidth: 0,
  },
  stackLabel: {
    color: color.neutral60,
    flexShrink: 0,
    width: 100,
  },
  placeholder: {
    color: PLACEHOLDER,
  },
  edit: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 20,
    height: 32,
    borderWidth: 0,
    borderRadius: 4,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 0,
  },
});
